export const CATEGORIES = [
  { id: "all", label: "All Records" },
  { id: "lab", label: "Lab Results" },
  { id: "prescriptions", label: "Prescriptions" },
  { id: "scans", label: "X-Rays / Scans" },
  { id: "pharmacy", label: "Pharmacy" },
];

const CATEGORY_LABELS = {
  lab: "Lab Results",
  prescriptions: "Prescriptions",
  scans: "X-Rays / Scans",
  pharmacy: "Pharmacy",
};

const inferFileType = (url) => {
  const ext = String(url || "").split(".").pop()?.toLowerCase().split("?")[0];
  if (ext === "pdf") return "pdf";
  if (["jpg", "jpeg", "png", "webp", "gif"].includes(ext)) return "jpg";
  return "other";
};

const titleFromUrl = (url, fallback) => {
  if (fallback) return fallback;
  const name = String(url || "").split("/").pop()?.split("?")[0] || "Medical Record";
  return name.replace(/[-_]/g, " ").replace(/\.\w+$/, "") || "Medical Record";
};

const parseDate = (value, fallback) => {
  if (value) {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d.toISOString();
  }
  return fallback || new Date().toISOString();
};

const mapUploadType = (type) => {
  const t = String(type || "").toLowerCase();
  if (/lab|result|diagnostic|test/.test(t)) return "lab";
  if (/prescription|rx/.test(t)) return "prescriptions";
  if (/x-ray|xray|scan|imaging/.test(t)) return "scans";
  if (/pharmacy|medicine|drug/.test(t)) return "pharmacy";
  return "lab";
};

const pushRecord = (records, seen, entry) => {
  const fileUrl = entry.fileUrl || entry.url || entry.file || entry.path;
  if (!fileUrl || !String(fileUrl).trim()) return;
  const key = String(fileUrl);
  if (seen.has(key)) return;
  seen.add(key);
  records.push({
    id: entry.id || key,
    title: entry.title || titleFromUrl(fileUrl),
    category: entry.category || "lab",
    provider: entry.provider || "Elthium Health",
    date: parseDate(entry.date, entry.fallbackDate),
    fileUrl: key,
    fileType: inferFileType(key),
  });
};

const extractFileEntries = (items, category, provider, fallbackDate, titlePrefix) => {
  if (!Array.isArray(items)) return [];
  return items
    .map((item, index) => {
      if (typeof item === "string") {
        return { fileUrl: item, category, provider, fallbackDate, title: titleFromUrl(item, titlePrefix) };
      }
      const fileUrl = item.fileUrl || item.url || item.file || item.path;
      return {
        id: item._id || item.id || `${category}-${index}`,
        fileUrl,
        category: mapUploadType(item.recordType || item.category || item.type) || category,
        provider: item.provider || item.labName || provider,
        date: item.dateOfRecord || item.date || item.createdAt,
        fallbackDate,
        title: item.title || item.name || item.fileName || titleFromUrl(fileUrl, titlePrefix),
      };
    })
    .filter((e) => e.fileUrl);
};

const bookingProvider = (booking) =>
  booking.diagnostic?.name ||
  booking.doctor?.name ||
  booking.package?.name ||
  "Elthium Health";

const bookingDate = (booking) => booking.date || booking.createdAt;

const isScanBooking = (booking) => {
  if (booking.cartItems?.some((i) => /scan|x-ray|xray/i.test(i.title || ""))) return true;
  if (booking.items?.some((i) => /scan|x-ray|xray/i.test(i.title || i.name || ""))) return true;
  return /scan|x-ray|xray/i.test(booking.serviceType || "");
};

// ponytail: category inference from booking fields + upload metadata; upgrade = dedicated records API
export const normalizeRecords = ({ bookings = [], staffProfile = null, prescriptionAppointments = [] } = {}) => {
  const records = [];
  const seen = new Set();

  bookings.forEach((booking, bIndex) => {
    const provider = bookingProvider(booking);
    const fallbackDate = bookingDate(booking);
    const scan = isScanBooking(booking);

    if (booking.reportFile?.trim()) {
      pushRecord(records, seen, {
        id: `report-${bIndex}`,
        fileUrl: booking.reportFile,
        category: scan ? "scans" : "lab",
        provider,
        date: fallbackDate,
        title: scan ? "Imaging Report" : "Lab Report",
      });
    }

    if (booking.diagPrescription?.trim()) {
      pushRecord(records, seen, {
        id: `diag-rx-${bIndex}`,
        fileUrl: booking.diagPrescription,
        category: "prescriptions",
        provider,
        date: fallbackDate,
        title: "Diagnostic Prescription",
      });
    }

    [
      { items: booking.receivedDiagReports, category: scan ? "scans" : "lab", title: "Lab Report" },
      { items: booking.receivedDiagPrescriptions, category: "prescriptions", title: "Prescription" },
      { items: booking.doctorReports, category: "lab", title: "Doctor Report" },
      { items: booking.doctorPrescriptions, category: "prescriptions", title: "Doctor Prescription" },
      { items: booking.userUploadedFiles, category: null, title: null },
    ].forEach(({ items, category, title }) => {
      extractFileEntries(items, category, provider, fallbackDate, title).forEach((entry) => {
        pushRecord(records, seen, entry);
      });
    });
  });

  prescriptionAppointments.forEach((appointment, index) => {
    const provider = appointment.doctor?.name || appointment.doctor_name || "Doctor";
    const fallbackDate = appointment.date || appointment.createdAt;
    extractFileEntries(
      appointment.doctorPrescriptions,
      "prescriptions",
      provider,
      fallbackDate,
      "Doctor Prescription"
    ).forEach((entry) => {
      pushRecord(records, seen, { ...entry, id: entry.id || `rx-${index}` });
    });
  });

  const staffFiles =
    staffProfile?.userUploadedFiles ||
    staffProfile?.uploadedFiles ||
    staffProfile?.files ||
    [];
  extractFileEntries(staffFiles, "lab", "Elthium Health", null, null).forEach((entry) => {
    pushRecord(records, seen, entry);
  });

  return records.sort((a, b) => new Date(b.date) - new Date(a.date));
};

export const categoryLabel = (id) => CATEGORY_LABELS[id] || id;

export const formatRecordDate = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

export const countByCategory = (records) => {
  const counts = { all: records.length, lab: 0, prescriptions: 0, scans: 0, pharmacy: 0 };
  records.forEach((r) => {
    if (counts[r.category] != null) counts[r.category] += 1;
  });
  return counts;
};

export const filterAndSortRecords = (records, { category, search, sort }) => {
  let list = records;
  if (category && category !== "all") {
    list = list.filter((r) => r.category === category);
  }
  if (search?.trim()) {
    const q = search.trim().toLowerCase();
    list = list.filter(
      (r) =>
        r.title.toLowerCase().includes(q) ||
        r.provider.toLowerCase().includes(q) ||
        categoryLabel(r.category).toLowerCase().includes(q)
    );
  }
  const sorted = [...list];
  if (sort === "date-asc") {
    sorted.sort((a, b) => new Date(a.date) - new Date(b.date));
  } else if (sort === "name") {
    sorted.sort((a, b) => a.title.localeCompare(b.title));
  } else {
    sorted.sort((a, b) => new Date(b.date) - new Date(a.date));
  }
  return sorted;
};
